import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getPlatformSpecs } from '../../constants/platformSpecs';
import CharacterCounter from './CharacterCounter';
import ImageCropPreview from './ImageCropPreview';
import styles from './FacebookPreview.module.scss';

const FacebookPreview = ({
  user,
  content,
  title,
  imageUrl,
  onRemoveImage,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const specs = getPlatformSpecs('facebook');
  const truncateAt = specs?.previewTruncateAt || 477;

  const stripHtml = (html) => {
    if (!html) return '';
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/div>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ');

    const tmp = document.createElement('DIV');
    tmp.innerHTML = text;
    text = tmp.textContent || tmp.innerText || '';

    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const plainContent = stripHtml(content);
  const charCount = plainContent.length;
  const shouldTruncate = charCount > truncateAt && !isExpanded;
  const displayContent = shouldTruncate
    ? `${plainContent.slice(0, truncateAt)}...`
    : plainContent;

  const getUserName = () => {
    if (!user) return 'User';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.email || 'User';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.first_name || user.name?.split(' ')[0] || '';
    const lastName = user.last_name || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  return (
    <div className={styles.facebookPreview}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.avatar}>
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={getUserName()} />
          ) : (
            <span>{getUserInitials()}</span>
          )}
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{getUserName()}</div>
          <div className={styles.postMeta}>
            <span>Just now</span>
            <span className={styles.dot}>·</span>
            {/* Facebook Globe Icon */}
            <svg className={styles.globeIcon} viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 0a8 8 0 108 8 8 8 0 00-8-8zm5.91 7H11.5a14.27 14.27 0 00-.93-4.32A6.02 6.02 0 0113.91 7zM8 14a11.43 11.43 0 01-1.53-5H9.53A11.43 11.43 0 018 14zm-1.53-7A11.43 11.43 0 018 2a11.43 11.43 0 011.53 5zM5.43 2.68A14.27 14.27 0 004.5 7H2.09a6.02 6.02 0 013.34-4.32zM2.09 9H4.5a14.27 14.27 0 00.93 4.32A6.02 6.02 0 012.09 9zm8.48 4.32A14.27 14.27 0 0011.5 9h2.41a6.02 6.02 0 01-3.34 4.32z" />
            </svg>
          </div>
        </div>
        <button className={styles.moreButton} aria-label="More options">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        <div className={styles.text}>
          {displayContent}
          {charCount > truncateAt && (
            <span
              className={styles.seeMore}
              onClick={() => setIsExpanded(!isExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'See less' : 'See more'}
            </span>
          )}
        </div>
      </div>

      {/* Image */}
      {imageUrl && (
        <div className={styles.imageContainer}>
          <ImageCropPreview
            imageUrl={imageUrl}
            platformType="facebook"
            onRemove={onRemoveImage}
          />
        </div>
      )}

      {/* Reactions Row */}
      <div className={styles.engagementSummary}>
        <div className={styles.reactions}>
          {/* Facebook Like (Blue) */}
          <span className={styles.reactionIcon} style={{ zIndex: 3 }}>
            <svg viewBox="0 0 16 16" width="18" height="18">
              <defs>
                <linearGradient id="fbLikeGradient" x1="50%" x2="50%" y1="0%" y2="100%">
                  <stop offset="0%" stopColor="#18AFFF" />
                  <stop offset="100%" stopColor="#0062DF" />
                </linearGradient>
              </defs>
              <circle cx="8" cy="8" r="8" fill="url(#fbLikeGradient)" />
              <path fill="#fff" d="M12.162 7.338c.176-.123.338-.245.338-.674 0-.43-.229-.604-.474-.725a.73.73 0 01-.089-.047c-.013-.03-.028-.057-.045-.083a.64.64 0 01-.069-.136c.016-.111.016-.263-.009-.389-.028-.134-.091-.282-.207-.406a.81.81 0 00-.112-.098c-.034-.104-.143-.272-.288-.42a1.1 1.1 0 00-.199-.159.8.8 0 00-.119-.064c-.033-.04-.074-.093-.117-.15a1.3 1.3 0 00-.24-.252c-.12-.097-.258-.158-.399-.158H7.5c-.3 0-.5.1-.7.3l-1.474 1.2c-.156.155-.323.41-.323.7v2.85c0 .375.3.675.675.675h2.74c.166 0 .315-.066.427-.177l.396-.252c.089.03.179.05.27.05h.51c.15 0 .3-.05.4-.15l.2-.2a.54.54 0 00.15-.4c0-.05 0-.1-.01-.15z" transform="translate(2, 2) scale(0.75)" />
            </svg>
          </span>
          {/* Facebook Love (Red) */}
          <span className={styles.reactionIcon} style={{ zIndex: 2, marginLeft: '-4px' }}>
            <svg viewBox="0 0 16 16" width="18" height="18">
              <defs>
                <linearGradient id="fbLoveGradient" x1="50%" x2="50%" y1="0%" y2="100%">
                  <stop offset="0%" stopColor="#FF6680" />
                  <stop offset="100%" stopColor="#E61739" />
                </linearGradient>
              </defs>
              <circle cx="8" cy="8" r="8" fill="url(#fbLoveGradient)" />
              <path fill="#fff" d="M8 13s-4.5-3.5-4.5-6.5c0-1.7 1.3-3 3-3 1 0 1.5.5 1.5.5s.5-.5 1.5-.5c1.7 0 3 1.3 3 3 0 3-4.5 6.5-4.5 6.5z" transform="translate(0, -0.5)" />
            </svg>
          </span>
        </div>
      </div>

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <button className={styles.actionButton}>
          {/* Facebook Like Icon */}
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.actionIcon}>
            <path d="M18.77 11h-4.23l1.52-4.94C16.38 5.03 15.54 4 14.38 4c-.58 0-1.14.24-1.52.65L7 11H3v10h4.77c.67 0 1.31-.23 1.82-.66l5.08-4.22c.3-.25.48-.6.53-.98l.46-3.45c.1-.65-.07-1.31-.47-1.83-.4-.52-.99-.86-1.65-.86z" />
          </svg>
          <span>Like</span>
        </button>
        <button className={styles.actionButton}>
          {/* Facebook Comment Icon */}
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.actionIcon}>
            <path d="M12 2C6.48 2 2 6.04 2 11c0 2.58 1.14 4.9 2.94 6.52-.15.85-.68 2.56-2.6 4.4.05.05 4.58-.9 6.66-2.42.94.23 1.94.36 2.98.36 5.52 0 10-4.04 10-9s-4.48-9-10-9z" />
          </svg>
          <span>Comment</span>
        </button>
        <button className={styles.actionButton}>
          {/* Facebook Share Icon */}
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.actionIcon}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.66-.52.36-1 .53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.03-.24.37-.49 1.02-.74 4-1.73 6.67-2.88 8.02-3.44 3.81-1.57 4.61-1.84 5.12-1.85.11 0 .37.03.54.17.14.12.18.28.2.45-.01.06-.01.24-.02.38z" />
          </svg>
          <span>Share</span>
        </button>
      </div>

      {/* Character Counter */}
      <div className={styles.counterSection}>
        <CharacterCounter charCount={charCount} platformType="facebook" />
      </div>
    </div>
  );
};

FacebookPreview.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  content: PropTypes.string,
  title: PropTypes.string,
  imageUrl: PropTypes.string,
  onRemoveImage: PropTypes.func,
};

export default FacebookPreview;
